import React, { useState, useEffect } from 'react';
import { createMarkup } from '../../../../../../Foundation/React/code/Utilities/HelperUtilities.js';
import { fetchPromise } from './apiHelper.ts';
import Orderline from './Orderline.tsx';
import Checkbox from '../../../../../../Foundation/Common/code/Views/Foundation/Common/Checkbox.js';
import Notification from '../../../../../../Foundation/Common/code/Views/Foundation/Common/Notifications/Notification.js';
import Spinner from '../../../../../../Foundation/Common/code/Views/Foundation/Common/Spinner.js';
import OrderDetails from './OrderDetails.tsx';
import { Translations, OrderData, OrderLine, ReturnRequestData, ReturnRequestLine, ReturnResult } from './types.ts';

interface OrderProps {
  Translations: Translations;
  currentOrder: OrderData;
  showHomeDeliveryDesc: boolean;
  scrollToTop: () => void;
}

type GetReturnDataResult = ReturnRequestData | { noOrderLinesSelected: boolean } | null;

const Order: React.FC<OrderProps> = (props) => {
  const { Translations, currentOrder } = props;
  const [withdrawalCompleted, setWithdrawalCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDisclaimerError, setShowDisclaimerError] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [orderLines, setOrderLines] = useState<OrderLine[]>(currentOrder.OrderLines);
  const [returnResult, setReturnResult] = useState<ReturnResult | null>(null);
  const itemsToReturn = orderLines.filter((x) => x.QuantityToReturn > 0);
  const infoMessage = currentOrder.Messages?.find((m) => m.Type === 2)?.Content;

  useEffect(() => {
    if (disclaimerAccepted) setShowDisclaimerError(false);
  }, [disclaimerAccepted]);

  const placeReturn = () => {
    setErrorMessage('');
    setIsLoading(true);

    const requestData = getReturnData(itemsToReturn);

    // if return data is not valid
    if (!requestData) {
      setErrorMessage(Translations.InputValidationMessage);
      setIsLoading(false);
      return;
    }

    // if there are no order lines chosen
    if ('noOrderLinesSelected' in requestData && requestData.noOrderLinesSelected) {
      setErrorMessage(Translations.NoLinesSelectedMessage);
      setIsLoading(false);
      return;
    }

    if (!disclaimerAccepted) {
      setShowDisclaimerError(true);
      setIsLoading(false);
      return;
    }

    const url = '/api/sitecore/withdrawals/CreateWithdrawal';
    const options: RequestInit = {
      method: 'POST',
      body: JSON.stringify(requestData),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    };

    try {
      fetchPromise<ReturnResult>(url, options)
        .then((response) => {
          if (response.Success) {
            setReturnResult(response);
            sendAnalyticsForReturn(response.Lines);
            setWithdrawalCompleted(true);
            props.scrollToTop();
          } else if (response.Messages.length > 0 && response.Messages[0].Content) {
            setErrorMessage(response.Messages[0].Content);
          } else {
            setErrorMessage(Translations.GeneralErrorMessage);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    } catch (err) {
      console.log('error in post request');
      console.log(err);
      setIsLoading(false);
    }
  };

  const getReturnData = (linesToReturn: OrderLine[]): GetReturnDataResult => {
    const validated = validateLines(linesToReturn);
    if (!validated) return null;

    const orderLinesToReturn: ReturnRequestLine[] = linesToReturn.map((t) => ({
      LineId: t.LineId,
      Quantity: t.QuantityToReturn,
      ShipMethodIsHomeDelivery: t.ShipMethodIsHomeDelivery,
    }));

    if (orderLinesToReturn.length > 0) {
      return {
        OrderId: currentOrder.OrderId,
        LinesToReturn: orderLinesToReturn,
        ShopName: currentOrder.ShopName,
        UserId: currentOrder.UserId,
        CustomerId: currentOrder.CustomerId,
        IsWithdrawal: true,
      };
    } else {
      return { noOrderLinesSelected: true };
    }
  };

  const validateLines = (linesToReturn: OrderLine[]): boolean => {
    let validated = true;
    const allLines = [...orderLines];

    linesToReturn.forEach((ol) => {
      const quantityValidation = validateQuantity(ol.QuantityToReturn);
      if (!quantityValidation) validated = false;

      const returnLineIndex = allLines.findIndex((r) => r.LineId === ol.LineId);
      const line = { ...allLines[returnLineIndex] };

      line.QuantityError = !quantityValidation;
      allLines[returnLineIndex] = line;
    });
    setOrderLines(allLines);
    return validated;
  };

  const validateQuantity = (quantity: number): boolean => {
    return !isNaN(quantity);
  };

  const sendAnalyticsForReturn = (lines: ReturnResult['Lines']) => {
    const orderId = currentOrder.OrderId;
    const products = lines.map((line) => ({
      id: line.Sku,
      quantity: line.Quantity,
    }));
    // window.google.tagmanager.events.placeAReturn(orderId, products); //todo fix tagmanager
    void orderId;
    void products;
  };

  return (
    <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 mb-14">
      <>
        {withdrawalCompleted ? (
          <div id="return-result">
            <p className="paragraph--s text-color--gray80 mb-3">{Translations.OrderDetailsOrderNumberLabel}:</p>
            <p className="paragraph text-color--pitchBlack mb-5">{currentOrder.OrderConfirmationId}</p>
            <p className="paragraph--s text-color--gray80 mb-3">{Translations.ReturnDate}:</p>
            <p className="paragraph text-color--pitchBlack mb-5">
              {new Date().toLocaleDateString(window._THULEDATA.SitecoreLanguage)}
            </p>
            <h1 className="heading2 mb-9">{Translations.ReturnOrderResultHeader}</h1>
            <div
              className="instructions mb-9"
              dangerouslySetInnerHTML={createMarkup(Translations.ReturnOrderResultDescription)}
            />
            {(returnResult?.Lines?.length ?? 0) > 0 && (
              <section className="orderlines mb-7 mb-lg-9">
                {returnResult?.Lines.map((ol, i) => (
                  <Orderline {...props} ol={ol as unknown as OrderLine} i={i} isWithdrawn={true} key={ol.LineId} />
                ))}
              </section>
            )}
            <div className="return-summary p-8">
              <h2 className="heading4 mb-7 text-color--pitchBlack">{Translations.ReturnSummary}</h2>
              <div className="order-info-row mb-3">
                <span className="paragraph--s text-color--pitchBlack">{Translations.ReturnedProducts}</span>
                <span className="paragraph--s text-color--gray80">{returnResult?.ReturnProducts}</span>
              </div>
              {returnResult?.OriginalFreight && (
                <div className="order-info-row mb-3">
                  <span className="paragraph--s text-color--pitchBlack">{Translations.OriginalShipping}</span>
                  <span className="paragraph--s text-color--gray80">{returnResult.OriginalFreight}</span>
                </div>
              )}
              {returnResult?.ReturnFreight && (
                <div className="order-info-row mb-5">
                  <span className="paragraph--s text-color--pitchBlack">{Translations.ReturnFreight}</span>
                  <span className="paragraph--s text-color--gray80">{returnResult.ReturnFreight}</span>
                </div>
              )}
              {returnResult?.ReturnTaxTotal && (
                <div className="order-info-row mb-5">
                  <span className="paragraph--s text-color--pitchBlack">{Translations.Tax}</span>
                  <span className="paragraph--s text-color--gray80">{returnResult.ReturnTaxTotal}</span>
                </div>
              )}
              <hr />
              <div className="order-info-row">
                <span className="heading5">{Translations.ReturnTotal}</span>
                <span className="heading5">{returnResult?.ReturnTotal}</span>
              </div>
            </div>
          </div>
        ) : (
          <div id="return-form">
            <div>
              <h1 className="heading2 mb-8">{Translations.Heading}</h1>
              <div
                className="page-intro-text mb-12"
                dangerouslySetInnerHTML={createMarkup(Translations.ReturnOrderDescription)}
              />
              <OrderDetails {...props} />
              {infoMessage && (
                <div className="mb-5">
                  <Notification
                    Type={'information'}
                    Size={'lg'}
                    Message={infoMessage}
                    HideCloseBtn={true}
                    ShowIcon={true}
                  />
                </div>
              )}
            </div>
            <section className="orderlines mb-7 mb-lg-9">
              {orderLines.map((ol, i) => (
                <Orderline
                  {...props}
                  ol={ol}
                  i={i}
                  isWithdrawn={false}
                  setOrderLines={setOrderLines}
                  orderLines={orderLines}
                  key={ol.LineId}
                />
              ))}
            </section>
            <Checkbox
              id="disclaimerCheckbox"
              onChange={() => setDisclaimerAccepted(!disclaimerAccepted)}
              label={Translations.ReturnOrderDisclaimer}
              checked={disclaimerAccepted}
              errorMessage={showDisclaimerError ? Translations.ReturnPolicyValidationMessage : ''}
            />
            {errorMessage && (
              <div className="mt-5">
                <Notification
                  Type={'error'}
                  Size={'lg'}
                  Title={Translations.GeneralErrorHeader}
                  Message={errorMessage}
                  HideCloseBtn={true}
                  ShowIcon={true}
                />
              </div>
            )}
            <button
              onClick={() => placeReturn()}
              className="btn thule-btn-default w-100 my-5"
              disabled={isLoading || itemsToReturn?.length === 0 || !disclaimerAccepted}
            >
              {Translations.ReturnOrderButton}
              {isLoading && <Spinner />}
            </button>
            {itemsToReturn?.length > 0 && (
              <button
                className="btn thule-btn-secondary w-100"
                onClick={() => setOrderLines(currentOrder.OrderLines)}
                disabled={isLoading}
              >
                {Translations.Reset} ( {itemsToReturn?.length} )
              </button>
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default Order;
